import {cardMoveLine} from "./cardMoves.js";

// The board history shows minion hits alongside drinks; the alert overlay in
// drinkEvents.js deliberately does not, which is why this list is local.
function isHistoryLine(event) {
  return event?.type === "drink" || event?.type === "death" || event?.type === "minion";
}

// The lines one event adds under its card. A play logs every card it moved in a single
// "cards" event, so one slot of the event window holds a whole Stolleken; here each move
// becomes a line of its own.
function historyLines(event) {
  if (event?.type === "cards") {
    const moves = Array.isArray(event.moves) ? event.moves : [];
    return moves.map((move, index) => ({...move, id: `${event.id}:${index}`, type: "move", at: event.at}));
  }
  return isHistoryLine(event) ? [event] : [];
}

function createCardEntry(event) {
  return {
    id: event.id,
    at: event.at,
    playerName: event.playerName || "",
    cardName: event.cardName || "Kort",
    cardText: event.cardText || "",
    targetName: event.targetName || null,
    drinks: [],
  };
}

function createStandaloneDrinkEntry(event) {
  const fatigue = event.cause === "fatigue";
  return {
    id: event.id,
    at: event.at,
    playerName: "",
    cardName: fatigue ? "Slut på kort" : {minion: "Försvarare", move: "Kort"}[event.type] || "Klunkar",
    cardText: fatigue ? "Kortleken är tom. Varje tur utan kort kostar en klunk mer." : "",
    targetName: null,
    drinks: [event],
  };
}

// hiddenIds are plays the board is still holding back — queued, or a coin still in the
// air: their tickets, and every line under them, wait until the card has landed.
export function buildBoardHistory(events, limit = 8, hiddenIds = []) {
  if (!Array.isArray(events)) return [];
  const hidden = new Set(hiddenIds);
  const entries = [];
  let currentEntry = null;
  let skipping = false;

  events.forEach((event) => {
    if (event.type === "card") {
      skipping = hidden.has(event.id);
      currentEntry = createCardEntry(event);
      if (!skipping) entries.push(currentEntry);
      return;
    }
    if (skipping) return;
    historyLines(event).forEach((line) => {
      // Fatigue lands at a turn start, not under the last card anybody played: it gets
      // a ticket of its own, or it read as that card's doing.
      if (currentEntry && line.cause !== "fatigue") currentEntry.drinks.push(line);
      else entries.push(createStandaloneDrinkEntry(line));
    });
  });

  return entries.reverse().slice(0, limit);
}

// The oldest card play the board has not animated yet, or null. seenIds is the
// caller's memory of what it already showed; anything older than maxAge is stale.
// Oldest first, because a poll can land several plays at once and the board shows them
// one after another: newest-first played them backwards, each on top of the last.
export function nextCardAnimation(events, seenIds, now = Date.now(), maxAge = 5000) {
  return (events || [])
    .find((event) => event.type === "card" && now - event.at < maxAge && !seenIds.has(event.id)) || null;
}

// What a board log entry lists under its card. One area card at a full table is nine
// drinks plus the minions that stood in the way, and listed whole it grew the log row
// until the action bar fell off the screen. Past the limit the last line counts the rest.
// `move` marks a line about cards rather than sips, which the ticket prints in plain ink:
// print-red is for drinking only.
export function boardDrinkLines(drinks, limit = 3) {
  if (!Array.isArray(drinks)) return [];
  const lines = drinks.map((event) => ({key: event.id, text: formatDrinkLine(event), move: event.type === "move"}));
  if (lines.length <= limit) return lines;
  return [...lines.slice(0, limit - 1), {key: "more", text: `och ${lines.length - limit + 1} till`, move: false}];
}

export function formatDrinkLine(event) {
  if (event.type === "move") return cardMoveLine(event);
  if (event.type === "minion") {
    return event.killed
      ? `${event.minionName} skyddade ${event.playerName}, tog ${event.amount} och slogs ut`
      : `${event.minionName} skyddade ${event.playerName} och tog ${event.amount}`;
  }
  const suffix = event.amount === 1 ? "klunk" : "klunkar";
  if (event.cause === "fatigue") {
    const tired = `${event.playerName} drack ${event.amount} ${suffix} i utmattning`;
    return event.type === "death" ? `${tired} + en shot och åkte ut` : tired;
  }
  return event.type === "death"
    ? `${event.playerName} drack ${event.amount} ${suffix} + en shot och åkte ut`
    : `${event.playerName} drack ${event.amount} ${suffix}`;
}
